/**
 * Guild Theme System for DECKSTR.GG
 *
 * Sets <html data-guild="..."> which swaps the CSS variables behind Tailwind's
 * `purple-*` (primary) and `amber-*` (accent) palettes — see styles/guild-themes.css.
 * Default (purple + amber) = no attribute.
 *
 * Persistence: localStorage on every device; when signed in, also saved to the
 * account (Supabase auth user_metadata.guild_theme) and restored on sign-in.
 */

import { useSyncExternalStore } from 'react';
import { supabase } from './supabase';

export type GuildTheme =
    | 'default'
    | 'azorius' | 'orzhov' | 'boros' | 'selesnya' | 'dimir'
    | 'izzet' | 'simic' | 'rakdos' | 'golgari' | 'gruul';

// Swatch colors for the picker UI (shade 400 of each scale in guild-themes.css)
const SWATCH = {
    purple: '#c084fc',
    amber: '#fbbf24',
    white: '#e6e2ee',
    blue: '#4fc8ff',
    black: '#7f4fa8',
    red: '#ff2d4f',
    green: '#b8ff1f',
} as const;

export const GUILD_THEMES: { id: GuildTheme; name: string; colors: string; swatches: [string, string] }[] = [
    { id: 'default', name: 'Deckstr', colors: 'Purple + Amber', swatches: [SWATCH.purple, SWATCH.amber] },
    { id: 'azorius', name: 'Azorius', colors: 'White + Blue', swatches: [SWATCH.white, SWATCH.blue] },
    { id: 'orzhov', name: 'Orzhov', colors: 'White + Black', swatches: [SWATCH.white, SWATCH.black] },
    { id: 'boros', name: 'Boros', colors: 'Red + White', swatches: [SWATCH.red, SWATCH.white] },
    { id: 'selesnya', name: 'Selesnya', colors: 'Green + White', swatches: [SWATCH.green, SWATCH.white] },
    { id: 'dimir', name: 'Dimir', colors: 'Blue + Black', swatches: [SWATCH.blue, SWATCH.black] },
    { id: 'izzet', name: 'Izzet', colors: 'Blue + Red', swatches: [SWATCH.blue, SWATCH.red] },
    { id: 'simic', name: 'Simic', colors: 'Green + Blue', swatches: [SWATCH.green, SWATCH.blue] },
    { id: 'rakdos', name: 'Rakdos', colors: 'Red + Black', swatches: [SWATCH.red, SWATCH.black] },
    { id: 'golgari', name: 'Golgari', colors: 'Black + Green', swatches: [SWATCH.black, SWATCH.green] },
    { id: 'gruul', name: 'Gruul', colors: 'Red + Green', swatches: [SWATCH.red, SWATCH.green] },
];

const STORAGE_KEY = 'deckstr-guild-theme';
const VALID = new Set<string>(GUILD_THEMES.map(t => t.id));
const listeners = new Set<() => void>();

function readStored(): GuildTheme {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v && VALID.has(v) ? (v as GuildTheme) : 'default';
    } catch {
        return 'default';
    }
}

let current: GuildTheme = typeof window !== 'undefined' ? readStored() : 'default';

function apply(theme: GuildTheme) {
    if (typeof document === 'undefined') return;
    if (theme === 'default') {
        document.documentElement.removeAttribute('data-guild');
    } else {
        document.documentElement.setAttribute('data-guild', theme);
    }
}

/** Call once before React renders to avoid a flash of the default theme. */
export function initGuildTheme() {
    apply(current);

    // Account sync: on load / sign-in, the theme saved on the account wins over this device's choice.
    // (USER_UPDATED is ignored on purpose — our own saves trigger it, and a slow save of an
    //  older pick could otherwise overwrite a newer one.)
    supabase.auth.onAuthStateChange((event, session) => {
        if (event !== 'INITIAL_SESSION' && event !== 'SIGNED_IN') return;
        const saved = session?.user?.user_metadata?.guild_theme;
        if (typeof saved === 'string' && VALID.has(saved) && saved !== current) {
            setLocal(saved as GuildTheme);
        }
    });
}

function setLocal(theme: GuildTheme) {
    current = theme;
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* storage unavailable */ }
    apply(theme);
    listeners.forEach(l => l());
}

/**
 * Save the theme to the signed-in user's account (Supabase user_metadata.guild_theme).
 * Returns false if nobody is signed in.
 */
async function saveGuildThemeToAccount(theme: GuildTheme): Promise<boolean> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;
    if (session.user.user_metadata?.guild_theme === theme) return true;
    const { error } = await supabase.auth.updateUser({ data: { guild_theme: theme } });
    if (error) throw error;
    return true;
}

export type ThemeSaveResult = 'account' | 'device' | 'error';

/**
 * Apply a theme on this device and, if signed in, save it to the account.
 * Resolves to where it was saved: 'account', 'device' (not signed in), or 'error' (account save failed).
 */
export async function setGuildTheme(theme: GuildTheme): Promise<ThemeSaveResult> {
    setLocal(theme);
    try {
        return (await saveGuildThemeToAccount(theme)) ? 'account' : 'device';
    } catch (err) {
        console.warn('Could not save theme to account:', err);
        return 'error';
    }
}

export function useGuildTheme(): [GuildTheme, (t: GuildTheme) => Promise<ThemeSaveResult>] {
    const theme = useSyncExternalStore(
        (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
        () => current,
        () => 'default' as GuildTheme,
    );
    return [theme, setGuildTheme];
}
